import { useState, useCallback, useRef, useEffect } from 'react';
import LocationPicker from '@Utilities/LocationPicker';

/**
 * Reusable form-row wrapper around LocationPicker.
 * Validates location on blur via the server-side shared API and saves to XML.
 *
 * Props:
 *   translations        – translations object (must contain "Location", "CityStateZip",
 *                          "UseMyCurrentLocation", "GeolocationHint", "IsInvalid", "IsRequired")
 *   getTranslation(key) – optional helper that returns translated JSX/string
 *   locationData        – { City, State, PostalCode, Country, DisplayText }
 *   onLocationChanged   – callback receiving updated locationData object
 *   onValidation        – optional callback receiving { hasError: bool, errorKey: string, errorText: string }
 *   queryString         – query string to append to save endpoint (optional)
 *   highlightErrors     – external errors map (checks .LocationPicker) - used as override
 *   inputErrors         – external input error map (checks .LocationPicker) - used as override
 */
const LocationPickerField = (props) => {

    const [validationError, setValidationError] = useState('');
    const [hasError, setHasError] = useState(false);
    const blurTimerRef = useRef(null);
    const locationDataRef = useRef(props.locationData);

    useEffect(() => {
        locationDataRef.current.DisplayText = [locationDataRef.current.City, locationDataRef.current.State, locationDataRef.current.PostalCode, locationDataRef.current.Country].filter(Boolean).join(', ');
    }, []);

    // Keep ref in sync so blur handler always has the latest location data
    useEffect(() => {
        locationDataRef.current = props.locationData;
    }, [props.locationData]);

    const t = (key) => {
        if (props.getTranslation) return props.getTranslation(key);
        return props.translations ? props.translations[key] : key;
    };

    const validateAndSave = useCallback((locationOverride) => {
        const locationData = locationOverride || locationDataRef.current;
        if (!locationData) return;

        const xhr = new XMLHttpRequest();
        xhr.onload = function () {
            if (xhr.status === 200) {
                const response = JSON.parse(xhr.response);
                if (response.error === "locationRequired") {
                    const errorText = t("IsRequired").replace("{0}", t("Location"));
                    setHasError(true);
                    setValidationError(errorText);
                    if (props.onValidation) props.onValidation({ hasError: true, errorKey: "locationRequired", errorText: errorText });
                } else if (response.error) {
                    setHasError(true);
                    setValidationError(response.error);
                    if (props.onValidation) props.onValidation({ hasError: true, errorKey: "locationInvalid", errorText: response.error });
                } else {
                    setHasError(false);
                    setValidationError('');
                    if (props.onValidation) props.onValidation({ hasError: false, errorKey: "", errorText: "" });
                }
            }
        };

        xhr.open("POST", '/listinginput/Location/ValidateAndSaveLocation', true);
        xhr.setRequestHeader("Content-type", "application/json");
        xhr.send(JSON.stringify({
            Address: locationData.Address || '',
            City: locationData.City || '',
            State: locationData.State || '',
            StateShortName: locationData.StateShortName || '',
            CountryShortName: locationData.CountryShortName || '',
            PostalCode: locationData.PostalCode || '',
            Country: locationData.Country || '',
            DisplayText: locationData.DisplayText || ''
        }));
    }, [props.onValidation]);

    // Clear internal error state when external errors are removed (e.g. after successful 'Done' validation)
    const externalHasError = props.highlightErrors && props.highlightErrors.LocationPicker;
    useEffect(() => {
        if (!externalHasError) {
            setHasError(false);
            setValidationError('');
        }
    }, [externalHasError]);

    const externalErrorText = props.inputErrors && props.inputErrors.LocationPicker;
    const showError = externalHasError || hasError;
    const errorText = externalErrorText || validationError;

    return (
        <div className="form-row cf" key="LocationPickerField">
            <label className="label-left" htmlFor="LocationPicker">{t("Location")} <span>*</span></label>
            <div className="input-right">
                <LocationPicker
                    id="LocationPicker"
                    value={props.locationData.FormattedLocation || props.locationData.DisplayText || ''}
                    placeholder={t("CityStateZip")}
                    geolocationLabel={t("UseMyCurrentLocation")}
                    onLocationSelected={(location) => {
                        if (blurTimerRef.current) {
                            clearTimeout(blurTimerRef.current);
                            blurTimerRef.current = null;
                        }
                        const loc = {
                            Address: location.Address || '',
                            City: location.City,
                            State: location.State,
                            StateShortName: location.StateShortName || '',
                            PostalCode: location.PostalCode,
                            Country: location.Country,
                            CountryShortName: location.CountryShortName || '',
                            FormattedLocation: location.DisplayText,
                            DisplayText: location.DisplayText
                        };
                        props.onLocationChanged(loc);
                        validateAndSave(loc);

                        // California-specific email opt-out logic
                        if (props.onReceiveEmailsChanged) {
                            if (loc.Country === 'USA' && (loc.State || '').toUpperCase() !== 'CALIFORNIA' && props.showWeeklyUpdateSubscriptionCheckbox) {
                                props.onReceiveEmailsChanged(true);
                            } else {
                                props.onReceiveEmailsChanged(false);
                            }
                        }
                    }}
                    onFieldChanged={(value) => {
                        props.onLocationChanged({
                            Address: '',
                            City: '',
                            State: '',
                            PostalCode: '',
                            Country: '',
                            FormattedLocation: value,
                            DisplayText: '',
                            CountryShortName: '',
                            StateShortName: ''
                        });
                    }}
                    onBlur={() => {
                        if (blurTimerRef.current) {
                            clearTimeout(blurTimerRef.current);
                        }
                        blurTimerRef.current = setTimeout(() => {
                            blurTimerRef.current = null;
                            validateAndSave();
                        }, 300);
                        if (props.onBlur) props.onBlur();
                    }}
                    error={showError}
                    errorText={errorText}
                    ariaLabelledBy="LocationPicker"
                    geolocationHint={t("GeolocationHint")}
                    geoErrorText={t("GeoError")}
                />
            </div>
        </div>
    );
};

export default LocationPickerField;
global.LocationPickerField = LocationPickerField;
